<template>
  <div class="home mt-5">
    <b-container class="list-clients-wrapper">

      <!-- Usage Summary Cards -->
      <b-row class="mb-3">
        <b-col md="3">
          <b-card class="shadow-sm text-center">
            <div class="text-muted small">Tokens Hoje</div>
            <div class="h5 mb-0 mt-1">{{ formatNumber(summary.today.tokens) }}</div>
            <div class="text-muted small">{{ summary.today.messages }} msgs</div>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card class="shadow-sm text-center">
            <div class="text-muted small">Tokens 7 dias</div>
            <div class="h5 mb-0 mt-1">{{ formatNumber(summary.week.tokens) }}</div>
            <div class="text-muted small">{{ summary.week.messages }} msgs</div>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card class="shadow-sm text-center">
            <div class="text-muted small">Tokens Mês</div>
            <div class="h5 mb-0 mt-1">{{ formatNumber(summary.month.tokens) }}</div>
            <div class="text-muted small">{{ summary.month.messages }} msgs</div>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card class="shadow-sm text-center">
            <div class="text-muted small">Conversas Mês</div>
            <div class="h5 mb-0 mt-1">{{ summary.conversationsThisMonth }}</div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Top Users & Agents -->
      <b-row class="mb-3">
        <b-col md="6">
          <b-card class="shadow-sm" header="Top Usuários (mês)">
            <b-table :items="summary.topUsers" :fields="topUserFields" small show-empty
              empty-text="Sem dados" class="mb-0">
              <template #cell(totalTokens)="row">{{ formatNumber(row.value) }}</template>
            </b-table>
          </b-card>
        </b-col>
        <b-col md="6">
          <b-card class="shadow-sm">
            <template #header>
              Top Agentes (mes)
              <i class="fa fa-question-circle text-muted ml-1"
                v-b-tooltip.hover title="Custo estimado com base nos tokens gravados pelo chat. Pode diferir do dashboard da OpenAI, que inclui chamadas de teste e ingestao de arquivos."></i>
            </template>
            <b-table :items="summary.topAgents" :fields="topAgentFields" small show-empty
              empty-text="Sem dados" class="mb-0">
              <template #cell(totalTokens)="row">{{ formatNumber(row.value) }}</template>
            </b-table>
          </b-card>
        </b-col>
      </b-row>

      <!-- Token Limits -->
      <b-card class="shadow-sm mb-3" header="Limites de Tokens">
        <b-button size="sm" variant="primary" class="mb-2" @click="showLimitModal = true">
          + Novo Limite
        </b-button>
        <b-table :items="tokenLimits" :fields="limitFields" small show-empty
          empty-text="Nenhum limite configurado" class="mb-0">
          <template #cell(userId)="row">
            <span v-if="row.item.user">{{ row.item.user.name || row.item.user.email }}</span>
            <b-badge v-else variant="info">Global</b-badge>
          </template>
          <template #cell(monthlyLimit)="row">{{ formatNumber(row.value) }}</template>
          <template #cell(active)="row">
            <b-badge :variant="row.value ? 'success' : 'secondary'">
              {{ row.value ? 'Ativo' : 'Inativo' }}
            </b-badge>
          </template>
          <template #cell(actions)="row">
            <b-button size="sm" variant="outline-danger" @click="removeLimit(row.item.id)">
              <i class="fa fa-trash"></i>
            </b-button>
          </template>
        </b-table>
      </b-card>

      <!-- Conversations Table -->
      <b-card no-body class="shadow-sm">
        <b-card-header>
          <b-row>
            <b-col md="4">
              <b-form-input v-model="filters.agentId" placeholder="Filtrar por agente" size="sm" />
            </b-col>
            <b-col md="4">
              <b-form-input v-model="filters.dateFrom" type="date" size="sm" />
            </b-col>
            <b-col md="4">
              <b-form-input v-model="filters.dateTo" type="date" size="sm" />
            </b-col>
          </b-row>
        </b-card-header>

        <b-table :items="conversations" :fields="convFields" :busy="isLoading"
          stacked="md" show-empty small hover @row-clicked="openConversation">
          <template #table-busy>
            <div class="text-center text-default my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong> Carregando...</strong>
            </div>
          </template>
          <template #cell(user)="row">
            {{ row.item.user ? (row.item.user.name || row.item.user.email) : '-' }}
          </template>
          <template #cell(totalTokens)="row">{{ formatNumber(row.value) }}</template>
          <template #cell(createdAt)="row">{{ row.value | moment('DD/MM/YYYY HH:mm') }}</template>
        </b-table>

        <b-card-footer v-if="totalPages > 1">
          <b-pagination v-model="page" :total-rows="total" :per-page="perPage"
            align="center" size="sm" />
        </b-card-footer>
      </b-card>

      <!-- New Limit Modal -->
      <b-modal v-model="showLimitModal" title="Novo Limite de Tokens" @ok="createLimit" ok-title="Salvar">
        <b-form-group label="Tipo">
          <b-form-radio-group v-model="newLimit.type" :options="[
            { value: 'global', text: 'Global (todos os usuários)' },
            { value: 'individual', text: 'Individual' },
          ]" />
        </b-form-group>
        <b-form-group v-if="newLimit.type === 'individual'" label="Usuário">
          <b-form-select v-model="newLimit.userId" :options="userOptions">
            <template #first>
              <b-form-select-option :value="null" disabled>Selecione um usuario...</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>
        <b-form-group label="Limite Mensal (tokens)">
          <b-form-input v-model="newLimit.monthlyLimit" type="number" placeholder="500000" />
        </b-form-group>
        <b-form-group label="Bloquear quando exceder?">
          <b-form-checkbox v-model="newLimit.isBlocking">Sim, bloquear</b-form-checkbox>
        </b-form-group>
        <b-form-group label="Alerta em (%)">
          <b-form-input v-model="newLimit.warningPct" type="number" placeholder="80" />
        </b-form-group>
      </b-modal>

    </b-container>
  </div>
</template>

<script>
import axios from 'axios';
import {
  getConversations,
  getUsageSummary,
  getTokenLimits,
  setTokenLimit,
  deleteTokenLimit,
} from '../../services/adminChat';

export default {
  name: 'ChatMonitor',
  data() {
    return {
      isLoading: false,
      conversations: [],
      total: 0,
      page: 1,
      perPage: 20,
      totalPages: 1,
      filters: {
        agentId: '',
        dateFrom: '',
        dateTo: '',
      },
      summary: {
        today: { tokens: 0, messages: 0 },
        week: { tokens: 0, messages: 0 },
        month: { tokens: 0, messages: 0 },
        conversationsThisMonth: 0,
        topUsers: [],
        topAgents: [],
      },
      tokenLimits: [],
      users: [],
      showLimitModal: false,
      newLimit: {
        type: 'global',
        userId: null,
        monthlyLimit: 500000,
        isBlocking: true,
        warningPct: 80,
      },
      convFields: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'user', label: 'Usuário' },
        { key: 'agentId', label: 'Agente', sortable: true },
        { key: 'messageCount', label: 'Msgs', sortable: true },
        { key: 'totalTokens', label: 'Tokens', sortable: true },
        { key: 'lastModel', label: 'Modelo' },
        { key: 'createdAt', label: 'Data', sortable: true },
      ],
      topUserFields: [
        { key: 'userName', label: 'Usuário' },
        { key: 'totalTokens', label: 'Tokens' },
        { key: 'messageCount', label: 'Msgs' },
      ],
      topAgentFields: [
        { key: 'agentId', label: 'Agente' },
        { key: 'totalTokens', label: 'Tokens' },
        { key: 'messageCount', label: 'Msgs' },
        { key: 'estimatedCost', label: 'Custo Est. (USD)', formatter: (v) => v != null ? `$${v.toFixed(4)}` : '-' },
      ],
      limitFields: [
        { key: 'userId', label: 'Usuário' },
        { key: 'monthlyLimit', label: 'Limite Mensal' },
        { key: 'isBlocking', label: 'Bloqueia', formatter: (v) => v ? 'Sim' : 'Não' },
        { key: 'warningPct', label: 'Alerta %' },
        { key: 'active', label: 'Status' },
        { key: 'actions', label: 'Ações' },
      ],
    };
  },
  watch: {
    page() { this.loadConversations(); },
    'filters.agentId'() { this.page = 1; this.loadConversations(); },
    'filters.dateFrom'() { this.page = 1; this.loadConversations(); },
    'filters.dateTo'() { this.page = 1; this.loadConversations(); },
  },
  computed: {
    userOptions() {
      return this.users.map((u) => ({
        value: u.id,
        text: `${u.name || u.email} (ID: ${u.id})`,
      }));
    },
  },
  async mounted() {
    await Promise.all([
      this.loadConversations(),
      this.loadSummary(),
      this.loadLimits(),
      this.loadUsers(),
    ]);
  },
  methods: {
    async loadConversations() {
      this.isLoading = true;
      try {
        const params = { page: this.page, perPage: this.perPage };
        if (this.filters.agentId) params.agentId = this.filters.agentId;
        if (this.filters.dateFrom) params.dateFrom = this.filters.dateFrom;
        if (this.filters.dateTo) params.dateTo = this.filters.dateTo;
        const data = await getConversations(params);
        this.conversations = data.conversations;
        this.total = data.total;
        this.totalPages = data.totalPages;
      } catch (err) {
        this.$toast.error('Erro ao carregar conversas.');
      } finally {
        this.isLoading = false;
      }
    },
    async loadSummary() {
      try {
        this.summary = await getUsageSummary();
      } catch (err) {
        console.warn('Failed to load summary:', err.message);
      }
    },
    async loadLimits() {
      try {
        this.tokenLimits = await getTokenLimits();
      } catch (err) {
        console.warn('Failed to load limits:', err.message);
      }
    },
    async loadUsers() {
      try {
        const response = await axios.get('/users');
        this.users = (response.data && response.data.users) || response.data || [];
      } catch (err) {
        console.warn('Failed to load users:', err.message);
      }
    },
    async createLimit() {
      // Validate individual limit requires a valid user ID
      if (this.newLimit.type === 'individual') {
        if (!this.newLimit.userId) {
          this.$toast.error('Selecione um usuario para limite individual.');
          return;
        }
      }
      try {
        const uid = this.newLimit.type === 'individual' ? parseInt(this.newLimit.userId) : null;
        await setTokenLimit({
          userId: uid,
          monthlyLimit: parseInt(this.newLimit.monthlyLimit),
          isBlocking: this.newLimit.isBlocking,
          warningPct: parseInt(this.newLimit.warningPct) || 80,
        });
        this.$toast.success('Limite criado.');
        this.showLimitModal = false;
        await this.loadLimits();
      } catch (err) {
        this.$toast.error('Erro ao criar limite.');
      }
    },
    async removeLimit(id) {
      if (!confirm('Remover este limite?')) return;
      try {
        await deleteTokenLimit(id);
        this.$toast.success('Limite removido.');
        await this.loadLimits();
      } catch (err) {
        this.$toast.error('Erro ao remover limite.');
      }
    },
    openConversation(item) {
      this.$router.push({ name: 'conversation-detail', params: { id: item.id } });
    },
    formatNumber(n) {
      if (!n) return '0';
      return parseInt(n).toLocaleString('pt-BR');
    },
  },
};
</script>
